/**
 * PerfCard Component
 * Individual performance metric card with sparkline and status indicator
 */

import { Card, CardContent, Badge } from '@makanly/ui-kit';
import { AlertTriangle, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { Sparkline } from './Sparkline';
import {
  PerfMetric,
  formatLatency,
  getLatencyStatus,
  getLatencyBadgeVariant,
  getLatencyColor,
  LATENCY_THRESHOLDS,
} from '../types/perf.types';

interface PerfCardProps {
  metric: PerfMetric;
  icon?: React.ReactNode;
  description?: string;
  compact?: boolean;
}

export function PerfCard({ metric, icon, description, compact = false }: PerfCardProps) {
  const { displayName, current, status, history, rollingAvg30s, rollingAvg1min, alertLevel } =
    metric;

  // Determine trend (comparing current to 30s average)
  const trend =
    current !== null && rollingAvg30s !== null
      ? current > rollingAvg30s * 1.2
        ? 'up'
        : current < rollingAvg30s * 0.8
          ? 'down'
          : 'stable'
      : 'stable';

  const statusColors = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    unknown: 'bg-gray-400',
  };

  const statusTextColors = {
    green: 'text-green-600 dark:text-green-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    red: 'text-red-600 dark:text-red-400',
    unknown: 'text-gray-400',
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 py-2">
        {/* Status dot */}
        <div className={`w-2 h-2 rounded-full ${statusColors[status]}`} />

        {/* Name and value */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-primary truncate">{displayName}</span>
            {alertLevel !== 'none' && (
              <AlertTriangle
                className={`w-3 h-3 ${alertLevel === 'critical' ? 'text-red-500' : 'text-yellow-500'}`}
              />
            )}
          </div>
        </div>

        {/* Sparkline */}
        <Sparkline samples={history} width={80} height={24} showThresholds={false} />

        {/* Current value */}
        <span className={`text-sm font-mono w-16 text-right ${statusTextColors[status]}`}>
          {formatLatency(current)}
        </span>
      </div>
    );
  }

  return (
    <Card
      className={`relative overflow-hidden ${alertLevel === 'critical' ? 'ring-2 ring-red-500/50' : alertLevel === 'warning' ? 'ring-2 ring-yellow-500/50' : ''}`}
    >
      {/* Alert indicator bar */}
      {alertLevel !== 'none' && (
        <div
          className={`absolute top-0 left-0 right-0 h-1 ${
            alertLevel === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
          }`}
        />
      )}

      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {icon && <div className="text-text-secondary">{icon}</div>}
            <div>
              <h3 className="text-sm font-semibold text-text-primary">{displayName}</h3>
              {description && <p className="text-xs text-text-secondary">{description}</p>}
            </div>
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-2">
            {alertLevel !== 'none' && (
              <div
                className={`p-1 rounded ${
                  alertLevel === 'critical'
                    ? 'bg-red-100 dark:bg-red-950/50'
                    : 'bg-yellow-100 dark:bg-yellow-950/50'
                }`}
              >
                {alertLevel === 'critical' ? (
                  <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
                )}
              </div>
            )}
            <Badge variant={getLatencyBadgeVariant(status)} className="text-xs">
              {status === 'green'
                ? 'Good'
                : status === 'yellow'
                  ? 'Slow'
                  : status === 'red'
                    ? 'Critical'
                    : 'Unknown'}
            </Badge>
          </div>
        </div>

        {/* Current value with trend */}
        <div className="flex items-end justify-between mb-3">
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-bold ${statusTextColors[status]}`}>
              {formatLatency(current)}
            </span>
            {trend !== 'stable' && (
              <span
                className={`flex items-center text-xs ${
                  trend === 'up' ? 'text-red-500' : 'text-green-500'
                }`}
              >
                {trend === 'up' ? (
                  <TrendingUp className="w-3 h-3 mr-0.5" />
                ) : (
                  <TrendingDown className="w-3 h-3 mr-0.5" />
                )}
                {trend === 'up' ? 'Rising' : 'Falling'}
              </span>
            )}
          </div>
        </div>

        {/* Sparkline */}
        <div className="mb-3">
          <Sparkline samples={history} width={200} height={40} showThresholds={true} />
        </div>

        {/* Rolling averages */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-secondary rounded-md px-2 py-1">
            <span className="text-text-secondary">30s avg:</span>
            <span className={`ml-1 font-mono ${getLatencyColor(getLatencyStatus(rollingAvg30s))}`}>
              {formatLatency(rollingAvg30s)}
            </span>
          </div>
          <div className="bg-secondary rounded-md px-2 py-1">
            <span className="text-text-secondary">1m avg:</span>
            <span className={`ml-1 font-mono ${getLatencyColor(getLatencyStatus(rollingAvg1min))}`}>
              {formatLatency(rollingAvg1min)}
            </span>
          </div>
        </div>

        {/* Threshold legend (small) */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border text-xs text-text-secondary">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>&lt;{LATENCY_THRESHOLDS.GREEN}ms</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
            <span>
              {LATENCY_THRESHOLDS.GREEN}-{LATENCY_THRESHOLDS.YELLOW}ms
            </span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span>&gt;{LATENCY_THRESHOLDS.YELLOW}ms</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Compact card variant for dashboard grid
 */
interface PerfCardMiniProps {
  metric: PerfMetric;
  onClick?: () => void;
}

export function PerfCardMini({ metric, onClick }: PerfCardMiniProps) {
  const { displayName, current, status, history, alertLevel } = metric;

  const statusColors = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    unknown: 'bg-gray-400',
  };

  const statusTextColors = {
    green: 'text-green-600 dark:text-green-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    red: 'text-red-600 dark:text-red-400',
    unknown: 'text-gray-400',
  };

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${
        alertLevel === 'critical'
          ? 'ring-2 ring-red-500/50'
          : alertLevel === 'warning'
            ? 'ring-2 ring-yellow-500/50'
            : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="p-3">
        {/* Header with status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${statusColors[status]}`} />
            <span className="text-xs font-medium text-text-primary truncate">{displayName}</span>
          </div>
          {alertLevel !== 'none' && (
            <AlertTriangle
              className={`w-3 h-3 ${alertLevel === 'critical' ? 'text-red-500' : 'text-yellow-500'}`}
            />
          )}
        </div>

        {/* Value and sparkline */}
        <div className="flex items-center justify-between">
          <span className={`text-lg font-bold ${statusTextColors[status]}`}>
            {formatLatency(current)}
          </span>
          <Sparkline samples={history} width={60} height={20} showThresholds={false} />
        </div>
      </CardContent>
    </Card>
  );
}

export default PerfCard;
